import { ApolloCache } from '@apollo/client'
import {
  DossDataFormat,
  TableCellData,
  getFormulaColumnType,
  isDateColumn,
  isFormulaType,
  isValidArrayInnerType,
  safeParseFormatOptions,
} from '@doss/common-all'
import { uniqBy } from 'lodash'
import {
  CreateTableColumnInput,
  CreateTableColumnMutation,
  DataType,
  IndexType,
  UpdateTableColumnInput,
} from '~/common/generated/apollo-operations'
import { TranslationKeys } from '~/common/generated/i18n.generated'
import {
  enumOptionFormValuesFromColumn,
  isEnumColumnFormValues,
  toEnumOptionInputs,
} from '~/components/table-columns/enum-option-form-values'
import { ColumnFormValues } from '~/components/table-columns/types'
import { ORDERED_OPTIONS, orderLookup } from '~/components/table-settings/constants'
import {
  SettingsPageColumn,
  newTableColumnFragment,
  newTableTabColumnFragment,
} from '~/components/tables/types'
import { toGraphQLLogicExpressionInput, toGraphQLParentFormulaInput } from './formula-input'

export type AllowedArrayInnerTypes =
  | DataType.ENUM
  | DataType.FILE_URL
  | DataType.LOOKUP
  | DataType.USER

// [PLAT-367] Move this to codegen.
export const ALLOWED_ARRAY_INNER_TYPES = [
  DataType.ENUM,
  DataType.LOOKUP,
  DataType.FILE_URL,
  DataType.USER,
] as const

type NewTableColumn = CreateTableColumnMutation['createTableColumn']
type NewTableColumnTabMember = NewTableColumn['tableTabs'][number]

/**
 * Determines if the icon should be shown based on the type and returns the result
 */
export const showIconInRecordForm = (columnType: DataType) => {
  const typesToShowIcon = [
    DataType.FORMULA_NUMBER,
    DataType.FORMULA_STRING,
    DataType.FORMULA_DATE,
    DataType.FORMULA_DATE_TIME,
    DataType.ID,
  ]
  return typesToShowIcon.includes(columnType)
}

/**
 * The concrete type persisted for a column. Formula columns derive their FORMULA_* subtype from
 * the parsed formula's inferred output type (the user only ever picks the generic "Formula" in
 * the dropdown); without a parse result they fall back to the numeric default. The backend
 * re-derives the output type independently and rejects the save on a mismatch.
 */
function resolveConcreteColumnType(column: ColumnFormValues): DataType {
  const parsedOutputType = column.formula?.outputType
  if (isFormulaType(column.type) && parsedOutputType) {
    return getFormulaColumnType(parsedOutputType) as DataType
  }
  return column.type as DataType
}

/**
 * Translate the create/update table column form data to the default value based on the
 * column's type and inner type
 */
function getColumnDefaultValue(column: ColumnFormValues): TableCellData | null {
  const defaultValue = column.defaultValue

  if (
    column.type === DataType.ARRAY &&
    column.innerType &&
    isValidArrayInnerType(column.innerType)
  ) {
    return defaultValue ?? { type: DossDataFormat.ARRAY, data: [] }
  }

  if (column.type === DataType.FILE_URL) {
    return { type: DossDataFormat.FILE }
  } else if (column.type === DataType.USER) {
    return { type: DossDataFormat.USER }
  } else if (column.type === DataType.LOOKUP) {
    return { type: DossDataFormat.LOOKUP }
  }

  return defaultValue ?? null
}

/**
 * Translate the form data to data for creating a table column
 */
export function prepareCreateColumnData(
  column: ColumnFormValues,
  tableTabId?: string,
): CreateTableColumnInput {
  // Prepare the base column data.
  const defaultValue = getColumnDefaultValue(column)

  // Remove the invoking states from the input
  const formulaInput = column.formula ?? null

  const lookupFilterExpressionInput = column.lookupFilterExpression ?? null

  const type = resolveConcreteColumnType(column)
  // FORMULA_STRING/FORMULA_BOOLEAN cannot hold any formatting at all (TableColumn check
  // constraint) — the form may still carry number/date formatting from before the formula
  // resolved to one of those subtypes. FORMULA_NUMBER and FORMULA_DATE/FORMULA_DATE_TIME are
  // exempted since they can legitimately hold formatting.
  const clearFormatting =
    isFormulaType(type) && type !== DataType.FORMULA_NUMBER && !isDateColumn(type)

  // Construct the object required for creation, ensuring all fields are explicitly assigned.
  const createColumnData: CreateTableColumnInput = {
    // Table id is not required in cases where the table is being created at the same time as
    // its columns
    tableId: column.tableId ?? '',
    name: column.name.trim(),
    description: column.description ?? '',
    type,
    innerType: column.innerType as DataType | null,
    isNullable: column.isNullable,
    isReadOnly: column.isReadOnly,
    // The flag only exists on NUMBER; the form keeps its last value across a type change, so
    // clear it here rather than trip the server's check constraint.
    isDecimal: type === DataType.NUMBER && column.isDecimal,
    formatPreset: clearFormatting ? null : (column.formatPreset ?? null),
    formatOptions: clearFormatting ? null : (column.formatOptions ?? null),
    indexType: column.indexType,
    indexPrefix: column.indexPrefix,
    indexSuffix: column.indexSuffix,
    indexAutogenerate: column.indexAutogenerate,
    displayImagesInline: column.displayImagesInline,
    enumOptionInputs: isEnumColumnFormValues(column)
      ? toEnumOptionInputs(column.enumOptionInputs ?? [])
      : undefined,
    defaultValue: defaultValue ?? null,
    tableTabId: tableTabId ?? null,
    formula:
      formulaInput && column.formulaString
        ? { ...toGraphQLParentFormulaInput(formulaInput), formulaString: column.formulaString }
        : null,
    lookupColumnId: column.lookupColumnId ?? null,
    lookupColumnGroupId: column.lookupColumnGroupId ?? null,
    lookupTableId: column.lookupTableId ?? null,
    lookupFilterExpression:
      lookupFilterExpressionInput && column.lookupFilterExpressionString
        ? {
            ...toGraphQLLogicExpressionInput(lookupFilterExpressionInput),
            logicExpressionString: column.lookupFilterExpressionString,
          }
        : null,
    showLookupBacklink: column.showLookupBacklink,
    lookupBacklinkName: column.lookupBacklinkName,
  }

  return createColumnData
}

/**
 * Translate the form data to data for updating a table column
 */
export function prepareUpdateColumnData(column: ColumnFormValues): UpdateTableColumnInput {
  // Validate mandatory fields.
  if (!column.id) {
    throw new Error("Missing required 'tableColumnId' for column update.")
  }

  // Remove the invoking states from the input
  const formulaInput = column.formula ?? undefined

  // Only allow deleting lookup filter expressions from Admin UI
  const lookupFilterExpressionInput = column.lookupFilterExpression ?? undefined

  const type = resolveConcreteColumnType(column)
  // FORMULA_STRING/FORMULA_BOOLEAN cannot hold any formatting at all (TableColumn check
  // constraint) — the form may still carry number/date formatting from before the formula
  // resolved to one of those subtypes. FORMULA_NUMBER and FORMULA_DATE/FORMULA_DATE_TIME are
  // exempted since they can legitimately hold formatting.
  const clearFormatting =
    isFormulaType(type) && type !== DataType.FORMULA_NUMBER && !isDateColumn(type)

  // Construct the object required for creation, ensuring all fields are explicitly assigned.
  const updateColumnData: UpdateTableColumnInput = {
    // Table id is not required in cases where the table is being created at the same time as
    // its columns
    tableColumnId: column.id,
    name: column.name.trim(),
    description: column.description,
    type,
    innerType: column.innerType as DataType | null,
    isNullable: column.isNullable,
    isReadOnly: column.isReadOnly,
    isDecimal: type === DataType.NUMBER && column.isDecimal,
    formatPreset: clearFormatting ? null : (column.formatPreset ?? null),
    formatOptions: clearFormatting ? null : (column.formatOptions ?? null),
    indexType: column.indexType,
    indexPrefix: column.indexPrefix,
    indexSuffix: column.indexSuffix,
    indexAutogenerate: column.indexAutogenerate,
    displayImagesInline: column.displayImagesInline,
    enumOptionInputs: isEnumColumnFormValues(column)
      ? toEnumOptionInputs(column.enumOptionInputs ?? [])
      : undefined,
    formula:
      formulaInput && column.formulaString
        ? { ...toGraphQLParentFormulaInput(formulaInput), formulaString: column.formulaString }
        : undefined,
    lookupColumnId: column.lookupColumnId,
    lookupColumnGroupId: column.lookupColumnGroupId,
    lookupTableId: column.lookupTableId,
    lookupFilterExpression:
      lookupFilterExpressionInput && column.lookupFilterExpressionString
        ? {
            ...toGraphQLLogicExpressionInput(lookupFilterExpressionInput),
            logicExpressionString: column.lookupFilterExpressionString,
          }
        : undefined,
    showLookupBacklink: column.showLookupBacklink,
    lookupBacklinkName: column.lookupBacklinkName,
  }

  return updateColumnData
}

/**
 * Translates column data to form data for a single column
 */
export const mapColumnToFormValues = (
  column: SettingsPageColumn,
  tableId: string,
): ColumnFormValues => {
  let defaultValue: TableCellData | null = null
  if (column.type === DataType.ARRAY) {
    defaultValue = { type: DossDataFormat.ARRAY, data: [] }
  } else if (column.type === DataType.BOOLEAN) {
    defaultValue = { type: DossDataFormat.BOOLEAN, data: false }
  }

  const parsedFormatOptions =
    column.formatPreset && column.formatOptions
      ? safeParseFormatOptions(column.formatPreset, column.formatOptions)
      : null

  return {
    ...column,
    // The concrete FORMULA_* subtype is kept in form state — collapsing it to generic FORMULA
    // here would lose the column's real type, so a metadata-only edit (where the formula is never
    // re-parsed) would fall back to FORMULA_NUMBER and silently rewrite a non-numeric formula
    // column. The type dropdown maps subtypes to the single "Formula" option at display time.
    type: column.type,
    tableId,
    defaultValue,
    formatPreset: column.formatPreset ?? null,
    formatOptions: parsedFormatOptions?.success ? parsedFormatOptions.data : null,
    formula: null,
    formulaString: column.formula?.formulaString,
    formulaId: column.formula?.id,
    enumOptions: column.enumOptions ?? [],
    enumOptionInputs: enumOptionFormValuesFromColumn(column.enumSet?.options, column.enumOptions),
    lookupColumnId: column.lookupColumn?.id ?? null,
    lookupColumnGroupId: column.lookupColumnGroup?.id ?? null,
    lookupTableId: null,
    lookupFilterExpression: null,
    lookupFilterExpressionString: column.lookupFilterExpression?.logicExpressionString,
    lookupFilterExpressionId: column.lookupFilterExpression?.id,
    showLookupBacklink: column.lookupColumnGroup?.showBacklink ?? false,
    lookupBacklinkName: column.lookupColumnGroup?.backlinkName ?? null,
    shouldCreateNewLookupGroup: false,
  }
}

/**
 * Returns the human readable type descriptor based on a column's type, innerType, and optionally
 * indexType
 */
export function getHumanReadableTableColumnDataType(
  t: (key: TranslationKeys) => string,
  type: DataType,
  innerType: AllowedArrayInnerTypes | null,
  indexType?: IndexType, // Only used to show the index type change in activity feed events
): string {
  switch (type) {
    case DataType.ARRAY: {
      switch (innerType) {
        case null:
          // Instead of throwing an error, return a fallback value
          return t('table.create_column.human_readable_types.array')
        case DataType.ENUM:
          return t('table.create_column.human_readable_types.array_enum')
        case DataType.FILE_URL:
          return t('table.create_column.human_readable_types.array_file_url')
        case DataType.LOOKUP:
          return t('table.create_column.human_readable_types.array_lookup')
        case DataType.USER:
          return t('table.create_column.human_readable_types.array_user')
      }
      break
    }
    case DataType.ENUM:
      return t('table.create_column.human_readable_types.enum')
    case DataType.FILE_URL:
      return t('table.create_column.human_readable_types.file_url')
    case DataType.USER:
      return t('table.create_column.human_readable_types.user')
    case DataType.STRING:
      return t('table.create_column.human_readable_types.string')
    case DataType.DATE_TIME:
      return t('table.create_column.human_readable_types.date_time')
    case DataType.BOOLEAN:
      return t('table.create_column.human_readable_types.boolean')
    case DataType.DATE:
      return t('table.create_column.human_readable_types.date')
    case DataType.LOOKUP:
      return t('table.create_column.human_readable_types.lookup')
    case DataType.NUMBER:
      return t('table.create_column.human_readable_types.number')
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return t('table.create_column.human_readable_types.formula')
    // Boolean-output formula columns are out of scope (no way to create one) — no label, which
    // also keeps the type out of the column-type dropdown options.
    case DataType.FORMULA_BOOLEAN:
      return ''
    case DataType.ID: {
      switch (indexType) {
        case undefined:
        case IndexType.NONE:
          return t('table.create_column.human_readable_types.id')
        case IndexType.NUMBER:
          return t('table.create_column.human_readable_types.id_number')
        case IndexType.STRING:
          return t('table.create_column.human_readable_types.id_string')
      }
    }
  }
}

/**
 * Get a list of all table column types in their human readable format
 */
export function getAllHumanReadableTableColumnDataTypes(
  t: (key: TranslationKeys) => string,
): string[] {
  const allTypes: string[] = []

  // Iterate over all DataType values
  for (const type of Object.values(DataType)) {
    // For ARRAY type, check inner types using ALLOWED_ARRAY_INNER_TYPES
    if (type === DataType.ARRAY) {
      for (const innerType of ALLOWED_ARRAY_INNER_TYPES) {
        allTypes.push(getHumanReadableTableColumnDataType(t, type, innerType))
      }
    } else {
      allTypes.push(getHumanReadableTableColumnDataType(t, type, null))
    }
  }

  // Sort the types based on the order defined in ORDERED_OPTIONS
  allTypes.sort((a, b) => {
    const indexA = orderLookup(t).get(a) ?? ORDERED_OPTIONS.length
    const indexB = orderLookup(t).get(b) ?? ORDERED_OPTIONS.length
    return indexA - indexB
  })

  return allTypes
}

/**
 * Adds a newly created table column to its table's cache
 */
export const addColumnToTable = <T extends object>(
  cache: ApolloCache<T>,
  table: { __typename: 'Table'; id: string },
  newColumn: NewTableColumn,
) => {
  const normalizedId = cache.identify(table)
  cache.updateFragment(
    {
      id: normalizedId,
      fragment: newTableColumnFragment,
      fragmentName: 'TableWithColumnsFragment',
    },
    (data) => {
      const updatedColumns = uniqBy([...data.columns, newColumn], (column) => column.id)
      return {
        ...data,
        columns: updatedColumns,
      }
    },
  )
}

/**
 * Adds a newly created table column to one of its table tab's cache
 */
export const addColumnToTableTab = <T extends object>(
  cache: ApolloCache<T>,
  newColumn: NewTableColumn,
  tableTabColumn: NewTableColumnTabMember,
) => {
  const tableTabNormalizedId = cache.identify(tableTabColumn.tableTab)
  cache.updateFragment(
    {
      id: tableTabNormalizedId,
      fragment: newTableTabColumnFragment,
    },
    (data) => {
      if (data) {
        const updatedColumns = uniqBy(
          [...data.columns, { ...tableTabColumn, tableColumn: newColumn }],
          (column) => column.id,
        )
        return {
          ...data,
          columns: updatedColumns,
        }
      }
    },
  )
}
